import { ClickEvent, TableSelectionEvent, Panel, Label, Button } from "@mcleod/components";
import { Api } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";

export interface SelectedRowData {
  profileId: string;
  transactionType: string;
}

export interface LayoutMapping {
  layout: any;
  transactionType: string;
}

export interface SnapshotResponseItem {
  filename: string;
  snapshot: string; // base64 encoded bytes
  error?: string;
}

export class ExportSnapshotManager {
  private readonly MAX_DOWNLOAD_RECORDS = 12;

  /**
   * Gets all tables from all tab layouts
   */
  public getAllTables(layouts: any[]) {
    return layouts.filter(table => table !== undefined && table !== null);
  }

  /**
   * Counts total selected records across all tables
   */
  public getTotalSelectedRecords(allTables: any[]): number {
    let totalSelected = 0;

    allTables.forEach(table => {
      if (table && table.selectedRows) {
        totalSelected += table.selectedRows.length;
      }
    });

    return totalSelected;
  }

  /**
   * Updates the download button state based on total selected records
   */
  public updateDownloadButtonState(buttonDownloadSnap: any, allTables: any[]): void {
    const totalSelected = this.getTotalSelectedRecords(allTables);
    const shouldDisable = totalSelected === 0 || totalSelected > this.MAX_DOWNLOAD_RECORDS;

    if (buttonDownloadSnap) {
      buttonDownloadSnap.enabled = !shouldDisable;

      // Update tooltip to reflect the current state
      if (totalSelected === 0) {
        buttonDownloadSnap.disabledTooltip = `Please select at least one record to download profile snapshots.`;
        buttonDownloadSnap.tooltip = 'Download partner profile snapshots.';
      } else if (totalSelected > this.MAX_DOWNLOAD_RECORDS) {
        buttonDownloadSnap.disabledTooltip = `Too many records selected (${totalSelected}). Maximum allowed is ${this.MAX_DOWNLOAD_RECORDS}.`;
        buttonDownloadSnap.tooltip = 'Download partner profile snapshots.';
      } else {
        buttonDownloadSnap.tooltip = `Download ${totalSelected} partner profile snapshot${totalSelected > 1 ? 's' : ''}.`;
      }
    }
  }

  /**
   * Sets up selection change handlers for all tables to monitor total selected records
   */
  public setupSelectionChangeHandlers(allTables: any[], updateCallback: () => void): void {
    allTables.forEach(table => {
      if (table) {
        table.addSelectionListener((event: TableSelectionEvent) => {
          updateCallback();
        });
      }
    });
  }

  /**
   * Collects selected profile data from all tables
   */
  private collectSelectedProfiles(layoutMappings: LayoutMapping[]): SelectedRowData[] {
    const selectedRowIds: SelectedRowData[] = [];

    layoutMappings.forEach(mapping => {
      if (mapping.layout && mapping.layout.selectedRows) {
        mapping.layout.selectedRows.forEach(row => {
          const id = row.data.get('id');
          if (id) {
            selectedRowIds.push({
              profileId: id,
              transactionType: mapping.transactionType
            });
          }
        });
      }
    });

    return selectedRowIds;
  }

  /**
   * Creates and manages the progress dialog
   */
  private createProgressDialog(selectedRowCount: number): {
    dialog: any,
    statusLabel: Label,
    progressLabel: Label,
    progressBarLabel: Label,
    closeButton: Button,
    updateProgressBar: (current: number, total: number) => void
  } {
    const dialogPanel = new Panel({
      padding: 20,
      minWidth: 400,
      minHeight: 250
    });

    const titleLabel = new Label({
      text: 'Downloading Profile Snapshots'
    });

    const statusLabel = new Label({
      text: 'Waiting for snapshots from server...'
    });

    const progressLabel = new Label({
      text: 'Step 1 of ' + (selectedRowCount + 1)
    });

    // Create visual progress bar using text characters
    const progressBarLabel = new Label({
      text: '🔵⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪⚪ 0%'
    });

    const closeButton = new Button({
      caption: 'OK',
      visible: false
    });

    dialogPanel.add(titleLabel);
    dialogPanel.add(statusLabel);
    dialogPanel.add(progressLabel);
    dialogPanel.add(progressBarLabel);
    dialogPanel.add(closeButton);

    // Helper function to update progress bar
    const updateProgressBar = (current: number, total: number) => {
      const percentage = Math.round((current / total) * 100);
      const barLength = 20;
      const filled = Math.round((current / total) * barLength);
      const empty = barLength - filled;

      const filledBar = '🔵'.repeat(filled);
      const emptyBar = '⚪'.repeat(empty);
      progressBarLabel.text = `${filledBar}${emptyBar} ${percentage}%`;
    };

    // Create and show progress dialog
    const progressDialog = CommonDialogs.createDialog(dialogPanel, {
      title: 'Download Progress',
      okVisible: false
    });

    return {
      dialog: progressDialog,
      statusLabel,
      progressLabel,
      progressBarLabel,
      closeButton,
      updateProgressBar
    };
  }

  /**
   * Processes the download response and creates files
   */
  private async processDownloadResponse(
    response: any,
    directoryHandle: any,
    progressComponents: any
  ): Promise<{ downloadedFiles: string[], errors: Array<{ filename: string, error: string }> }> {
    const { statusLabel, progressLabel, updateProgressBar } = progressComponents;
    const downloadedFiles: string[] = [];
    const errors: Array<{ filename: string, error: string }> = [];

    // Update status for server response completed
    statusLabel.text = 'Creating local snapshots...';
    progressLabel.text = 'Step 2 of ' + (response.data.length + 1);
    updateProgressBar(1, response.data.length + 1);

    // Iterate through the array response and collect downloaded filenames
    for (let i = 0; i < response.data.length; i++) {
      const item: SnapshotResponseItem = response.data[i];
      const filename: string = item.filename;

      // Update current step status
      statusLabel.text = `Processing: ${filename}`;
      progressLabel.text = `Step ${i + 2} of ${response.data.length + 1}`;
      updateProgressBar(i + 2, response.data.length + 1);

      // Check for errors or empty snapshot data
      if (item.error && item.error.trim().length > 0) {
        // Record the error and skip file creation
        errors.push({ filename, error: item.error });
        statusLabel.text = `Error processing: ${filename}`;
        continue;
      }

      // Check if snapshot data is empty (only if no server error was reported)
      if (!item.snapshot || item.snapshot.trim().length === 0) {
        // Record the error and skip file creation
        errors.push({ filename, error: "No snapshot data available" });
        statusLabel.text = `No data for: ${filename}`;
        continue;
      }

      // Convert base64 string to Uint8Array
      let contents: Uint8Array;
      try {
        const binaryString = atob(item.snapshot);
        contents = new Uint8Array(binaryString.length);
        for (let j = 0; j < binaryString.length; j++) {
          contents[j] = binaryString.charCodeAt(j);
        }

        // Check if the decoded data is empty
        if (contents.length === 0) {
          errors.push({ filename, error: "Empty snapshot data" });
          statusLabel.text = `Empty data for: ${filename}`;
          continue;
        }
      } catch (error) {
        errors.push({ filename, error: "Invalid snapshot data format" });
        statusLabel.text = `Invalid data for: ${filename}`;
        continue;
      }

      // Create file in the selected directory using File System Access API or fallback
      statusLabel.text = `Creating local snapshot: ${filename}`;
      const zipFilename = `${filename}.zip`;

      try {
        if (directoryHandle) {
          // Use File System Access API
          const fileHandle = await directoryHandle.getFileHandle(zipFilename, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(contents);
          await writable.close();
        } else {
          // Fallback: create download link for browser download
          const blob = new Blob([new Uint8Array(contents)], { type: 'application/zip' });
          const url = URL.createObjectURL(blob);

          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = zipFilename;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);
        }

        // Add to downloaded files list only if successful
        downloadedFiles.push(zipFilename);
      } catch (fileError) {
        errors.push({ filename, error: `Failed to create file: ${fileError.message || fileError}` });
        statusLabel.text = `Failed to create: ${filename}`;
      }
    }

    return { downloadedFiles, errors };
  }

  /**
   * Shows completion dialog with downloaded files and errors
   */
  private showCompletionDialog(
    downloadedFiles: string[],
    errors: Array<{ filename: string, error: string }>,
    directoryHandle: any,
    selectedPath: string
  ): void {
    let message = '';

    // Add location information
    const locationMessage = directoryHandle ?
      `Profile snapshots have been saved to: ${selectedPath}` :
      `Profile snapshots have been downloaded to your default download folder`;

    if (downloadedFiles.length > 0) {
      const fileList = downloadedFiles.map(file => `• ${file}`).join('\n');
      message += `${locationMessage}\n\nSuccessfully downloaded files:\n${fileList}`;
    }

    if (errors.length > 0) {
      const errorList = errors.map(error => `• ${error.filename}: ${error.error}`).join('\n');
      if (downloadedFiles.length > 0) {
        message += `\n\nFiles with errors:\n${errorList}`;
      } else {
        message = `No files were downloaded due to errors:\n\n${errorList}`;
      }
    }

    if (downloadedFiles.length === 0 && errors.length === 0) {
      message = 'No files were processed.';
    }

    const title = errors.length > 0 ? "Download Completed with Errors" : "Download Completed";
    CommonDialogs.showMessage(message, title);
  }

  /**
   * Performs the actual download with progress dialog
   */
  public async performDownloadWithProgress(directoryHandle: any, selectedPath: string, layoutMappings: LayoutMapping[]): Promise<void> {
    const selectedRowIds = this.collectSelectedProfiles(layoutMappings);

    const progressComponents = this.createProgressDialog(selectedRowIds.length);
    const { dialog: progressDialog, statusLabel, progressLabel, closeButton, updateProgressBar } = progressComponents;

    // Show dialog without awaiting so API call can proceed
    progressDialog.show();

    try {
      const response = await Api.post("lme/datafusion/download-profile-snapshot", {
        profile_data: selectedRowIds
      });

      const result = await this.processDownloadResponse(response, directoryHandle, progressComponents);
      const { downloadedFiles, errors } = result;

      // All processing completed
      if (errors.length > 0) {
        statusLabel.text = `Completed with ${errors.length} error${errors.length > 1 ? 's' : ''}`;
      } else {
        statusLabel.text = 'All snapshots downloaded successfully!';
      }
      progressLabel.text = `${selectedRowIds.length + 1} of ${selectedRowIds.length + 1}`;
      updateProgressBar(selectedRowIds.length + 1, selectedRowIds.length + 1); // 100%

      // Show OK button to close dialog
      closeButton.visible = true;

      // Show detailed completion message with downloaded files and errors after dialog is closed
      let completionDialogShown = false;
      const showCompletionDialog = () => {
        if (!completionDialogShown) {
          completionDialogShown = true;
          this.showCompletionDialog(downloadedFiles, errors, directoryHandle, selectedPath);
        }
      };

      closeButton.addClickListener(() => {
        progressDialog.close();
        showCompletionDialog();
      });

    } catch (error) {
      progressDialog.close();
      console.error('Error initiating download:', error);
      CommonDialogs.showError(
        "An error occurred while starting the download. Please try again.",
        "Download Error"
      );
    }
  }

  /**
   * Handles the download button click event
   */
  public async handleDownloadButtonClick(event: ClickEvent, layoutMappings: LayoutMapping[]): Promise<void> {
    try {
      // Check if the browser supports the File System Access API
      if ('showDirectoryPicker' in window) {
        // Use the modern File System Access API with default downloads directory
        const directoryHandle = await (window as any).showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'downloads' // Start in the user's Downloads folder
        });

        const selectedPath = directoryHandle.name;

        // Call the download method
        await this.performDownloadWithProgress(directoryHandle, selectedPath, layoutMappings);
      } else {
        // Fallback: Show input dialog for manual path entry with Downloads default
        const defaultPath = navigator.userAgent.includes('Windows') ? 'C:\\Users\\%USERNAME%\\Downloads' :
          navigator.userAgent.includes('Mac') ? '~/Downloads' :
          '~/Downloads';

        const directoryPath = await CommonDialogs.showInputDialog(
          "Choose a directory to download profile snapshots.\n\nEnter the full path to the directory:",
          "Select Download Directory",
          {
            placeholder: defaultPath,
            captionVisible: false,
            text: defaultPath // Pre-fill with downloads directory
          }
        );

        if (directoryPath) {
          // Call the download method with fallback (no directoryHandle)
          await this.performDownloadWithProgress(null, directoryPath, layoutMappings);
        }
      }
    } catch (error) {
      // User cancelled the dialog or an error occurred
      if (error.name !== 'AbortError') {
        console.error('Error selecting directory:', error);
        CommonDialogs.showError(
          "An error occurred while selecting the directory. Please try again.",
          "Directory Selection Error"
        );
      }
    }
  }
}
